/**
 * FeaturedTracksPanel Component
 *
 * Displays an artist's top 4 tracks from Spotify with:
 * - Play/pause control per track
 * - 30-second preview audio
 * - Animated waveform while playing
 * - Dark glass card styling with purple glow
 * - Mobile-first design
 *
 * Design principles:
 * - Ambient, luxury feel
 * - No Spotify branding/iframe
 * - Only one track plays at a time
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Music, Play, Pause, Loader2 } from 'lucide-react';
import {
  getArtistTopTracks,
  formatDuration,
  type SpotifyTrack,
  type SpotifyArtist,
} from '../services/spotifyService';

interface FeaturedTracksPanelProps {
  artistName: string;
  /** Theme accent color (default: purple) */
  accentColor?: 'purple' | 'orange' | 'sky' | 'red' | 'brand';
}

/**
 * Animated waveform bars component
 */
const WaveformAnimation: React.FC<{ isPlaying: boolean; accentColor: string }> = ({
  isPlaying,
  accentColor,
}) => {
  const barCount = 5;
  const bars = Array.from({ length: barCount });

  return (
    <div className="flex items-end gap-0.5 h-4 w-5">
      {bars.map((_, i) => (
        <div
          key={i}
          className={`w-0.5 rounded-full transition-all duration-150 ${accentColor} ${
            isPlaying ? 'animate-waveform' : 'h-1'
          }`}
          style={{
            animationDelay: isPlaying ? `${i * 0.1}s` : '0s',
            height: isPlaying ? undefined : '4px',
          }}
        />
      ))}
    </div>
  );
};

/**
 * Single track row component
 */
const TrackRow: React.FC<{
  track: SpotifyTrack;
  isPlaying: boolean;
  isLoading: boolean;
  onTogglePlay: () => void;
  accentColorClass: string;
  glowColorClass: string;
}> = ({ track, isPlaying, isLoading, onTogglePlay, accentColorClass, glowColorClass }) => {
  const hasPreview = !!track.previewUrl;

  return (
    <button
      onClick={onTogglePlay}
      disabled={!hasPreview || isLoading}
      className={`
        w-full flex items-center gap-3 p-3 rounded-xl transition-all duration-200
        ${hasPreview ? 'hover:bg-white/5 active:bg-white/10 cursor-pointer' : 'cursor-not-allowed opacity-60'}
        ${isPlaying ? `bg-white/10 ${glowColorClass}` : 'bg-white/[0.02]'}
      `}
      aria-label={isPlaying ? `Pause ${track.name}` : `Play ${track.name}`}
    >
      {/* Play/Pause Button or Waveform */}
      <div
        className={`
          w-10 h-10 flex-shrink-0 rounded-full flex items-center justify-center
          transition-all duration-200
          ${isPlaying ? accentColorClass : 'bg-white/10'}
        `}
      >
        {isLoading ? (
          <Loader2 size={18} className="text-white animate-spin" />
        ) : isPlaying ? (
          <WaveformAnimation isPlaying={true} accentColor="bg-white" />
        ) : (
          <Play size={18} className={`text-white ml-0.5 ${!hasPreview ? 'opacity-50' : ''}`} />
        )}
      </div>

      {/* Track Info */}
      <div className="flex-1 min-w-0 text-left">
        <p className={`text-sm font-medium truncate ${isPlaying ? 'text-white' : 'text-white/90'}`}>
          {track.name}
        </p>
        <p className="text-xs text-white/50 truncate">
          {track.albumName}
        </p>
      </div>

      {/* Duration */}
      <div className="flex-shrink-0 text-xs text-white/40 tabular-nums">
        {formatDuration(track.durationMs)}
      </div>
    </button>
  );
};

/**
 * FeaturedTracksPanel Component
 */
export const FeaturedTracksPanel: React.FC<FeaturedTracksPanelProps> = ({
  artistName,
  accentColor = 'purple',
}) => {
  const [artist, setArtist] = useState<SpotifyArtist | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [playingTrackId, setPlayingTrackId] = useState<string | null>(null);
  const [loadingTrackId, setLoadingTrackId] = useState<string | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  // Color classes based on accent color
  const colorClasses = {
    purple: {
      accent: 'bg-purple-500',
      glow: 'shadow-[0_0_20px_rgba(168,85,247,0.3)]',
      border: 'border-purple-500/30',
      text: 'text-purple-400',
      gradient: 'from-purple-500/20 to-purple-600/10',
    },
    orange: {
      accent: 'bg-orange-500',
      glow: 'shadow-[0_0_20px_rgba(249,115,22,0.3)]',
      border: 'border-orange-500/30',
      text: 'text-orange-400',
      gradient: 'from-orange-500/20 to-orange-600/10',
    },
    sky: {
      accent: 'bg-sky-500',
      glow: 'shadow-[0_0_20px_rgba(14,165,233,0.3)]',
      border: 'border-sky-500/30',
      text: 'text-sky-400',
      gradient: 'from-sky-500/20 to-sky-600/10',
    },
    red: {
      accent: 'bg-red-500',
      glow: 'shadow-[0_0_20px_rgba(239,68,68,0.3)]',
      border: 'border-red-500/30',
      text: 'text-red-400',
      gradient: 'from-red-500/20 to-red-600/10',
    },
    brand: {
      accent: 'bg-brand-500',
      glow: 'shadow-[0_0_20px_rgba(var(--accent-primary-rgb),0.3)]',
      border: 'border-brand-500/30',
      text: 'text-brand-400',
      gradient: 'from-brand-500/20 to-brand-600/10',
    },
  };

  const colors = colorClasses[accentColor];

  // Fetch artist tracks on mount or when artist name changes
  useEffect(() => {
    let cancelled = false;

    const fetchTracks = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const data = await getArtistTopTracks(artistName);
        if (!cancelled) {
          setArtist(data);
          if (!data) {
            setError('Could not find artist');
          }
        }
      } catch (err) {
        if (!cancelled) {
          setError('Failed to load tracks');
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    fetchTracks();

    return () => {
      cancelled = true;
    };
  }, [artistName]);

  // Cleanup audio on unmount
  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, []);

  // Handle track play/pause
  const handleTogglePlay = useCallback(
    async (track: SpotifyTrack) => {
      if (!track.previewUrl) return;

      // If this track is already playing, pause it
      if (playingTrackId === track.id) {
        audioRef.current?.pause();
        setPlayingTrackId(null);
        return;
      }

      // Stop any currently playing track
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }

      setLoadingTrackId(track.id);

      // Create new audio element
      const audio = new Audio(track.previewUrl);
      audioRef.current = audio;

      // Handle audio events
      audio.oncanplaythrough = () => {
        setLoadingTrackId(null);
        setPlayingTrackId(track.id);
        audio.play().catch(() => {
          setPlayingTrackId(null);
        });
      };

      audio.onended = () => {
        setPlayingTrackId(null);
      };

      audio.onerror = () => {
        setLoadingTrackId(null);
        setPlayingTrackId(null);
      };

      audio.load();
    },
    [playingTrackId]
  );

  // Don't render if loading or error (silent fail)
  if (isLoading) {
    return (
      <div
        className={`
          bg-gradient-to-br ${colors.gradient}
          backdrop-blur-xl rounded-xl border ${colors.border}
          p-4 transition-all duration-200
        `}
      >
        <div className="flex items-center gap-3 mb-4">
          <div className={`w-8 h-8 ${colors.accent} rounded-lg flex items-center justify-center`}>
            <Music size={18} className="text-white" />
          </div>
          <h3 className="text-sm font-semibold text-white uppercase tracking-wide">
            Featured Tracks
          </h3>
        </div>
        <div className="flex items-center justify-center py-8">
          <Loader2 size={24} className={`${colors.text} animate-spin`} />
        </div>
      </div>
    );
  }

  // Don't render if no tracks found
  if (!artist || artist.topTracks.length === 0) {
    return null;
  }

  return (
    <div
      className={`
        bg-gradient-to-br ${colors.gradient}
        backdrop-blur-xl rounded-xl border ${colors.border}
        p-4 transition-all duration-200
        ${playingTrackId ? colors.glow : ''}
      `}
    >
      {/* Header */}
      <div className="flex items-center gap-3 mb-4">
        <div className={`w-8 h-8 ${colors.accent} rounded-lg flex items-center justify-center`}>
          <Music size={18} className="text-white" />
        </div>
        <div>
          <h3 className="text-sm font-semibold text-white uppercase tracking-wide">
            Featured Tracks
          </h3>
          <p className="text-xs text-white/50">{artist.name}</p>
        </div>
      </div>

      {/* Track List */}
      <div className="space-y-1">
        {artist.topTracks.map((track) => (
          <TrackRow
            key={track.id}
            track={track}
            isPlaying={playingTrackId === track.id}
            isLoading={loadingTrackId === track.id}
            onTogglePlay={() => handleTogglePlay(track)}
            accentColorClass={colors.accent}
            glowColorClass={colors.glow}
          />
        ))}
      </div>

      {/* Footer hint */}
      <p className="text-[10px] text-white/30 text-center mt-4 uppercase tracking-wider">
        30-second previews
      </p>
    </div>
  );
};

export default FeaturedTracksPanel;
